import { useEffect, useMemo, useState } from "react";
import CampOverridesViewModel from "../Models/CampOverridesViewModel";
import { SubLineItemDto } from "../Models";

const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatDateString = (date: Date): string => {
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    const ordinalSuffix = getOrdinalSuffix(day);
    
    return `${dayOfWeek} ${month} ${day}${ordinalSuffix} ${year}`;
};

export default () => {
    const [history, setHistory] = useState<SubLineItemDto[] | undefined>();

    const [campOverrides, setCampOverrides] = useState<CampOverridesViewModel | undefined>();

    useEffect(() => {
        Promise.all([
            getClassesNEvents(window.baseApiUrl),
            getCampOverrides(window.baseApiUrl)
        ]).then(([eventsData, campOverridesData]) => {
            setHistory(eventsData);
            setCampOverrides(campOverridesData);
        }).catch((error) => {
            console.error('Error fetching classes and events:', error);
            // Handle the error as needed, e.g., show a notification or set an error state
        });
    }, []);

    const availableDates = useMemo(() => {
        if (!history || !campOverrides) return;
        // Get the distinct dates from the events
        let events: SubLineItemDto[] = history;
        events.forEach(ev => {
            if (!ev.performanceDateTime) return;
            let override = campOverrides.classOverrides?.find(co => co.packageOrProductionSeasonId === ev.productionSeasonId);
            if (!override) 
                override = campOverrides.campOverrides?.find(co => co.packageOrProductionSeasonId === ev.packageId);
            ev.override = override;
            ev.formattedPerformanceDate = formatDateString(new Date(ev.performanceDateTime || ''));
        });
        const dates = new Set<string>();
        for (var ev of history) {
            if (!ev.performanceDateTime) continue;
            const date = new Date(ev.performanceDateTime);
            const formattedDate = formatDateString(date);
            dates.add(formattedDate);
        }
        return [...dates].map(date => ({
            date,
            events: history
                .filter(ev => ev.formattedPerformanceDate === date)
                .reduce((acc, ev) => {                    
                    if (!acc.some(e => e.performanceId === ev.performanceId)) {
                        if (ev.recipientDisplayName && ev.recipientDisplayName.length > 0) {
                            ev.children = [ev.recipientDisplayName];
                        } else {
                            ev.children = [];
                        }
                        acc.push(ev);
                    } else {
                        var accIndex = acc.findIndex(e => e.performanceId === ev.performanceId);
                        if (accIndex > -1 && ev && ev.recipientDisplayName && ev.recipientDisplayName.length > 0) {
                            acc[accIndex].children = [...new Set([...(acc[accIndex].children ?? []), ev.recipientDisplayName])];
                        }
                    }
                    return acc;
                }, [] as SubLineItemDto[])

        }));
    }, [history, campOverrides]);

    const getClassesNEvents = async (fetchBase: string): Promise<SubLineItemDto[]> => {
        try {
            const response = await fetch(`${fetchBase}/api/tessitura/classes-events`, {
                'credentials': 'include',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            // Parse the JSON body of the response
            const data = await response.json();
            // Return the populated map
            return data;
        } catch (error) {
            console.error('Fetch failed:', error);
            throw error; // rethrow the error to handle it further up
        }
    }

    const getCampOverrides = async (fetchBase: string): Promise<CampOverridesViewModel> => {
        try {
            const response = await fetch(`${fetchBase}/api/overrides`, {
                'credentials': 'include',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            // Parse the JSON body of the response
            const data = await response.json();
            // Return the populated map
            return data;
        } catch (error) {
            console.error('Fetch failed:', error);
            throw error; // rethrow the error to handle it further up
        }
    }
    return { availableDates };
}